"use client"

import type { ReactNode } from "react"
import { Download, Printer, ArrowLeft } from "lucide-react"
import Image from "next/image"
import Link from "next/link"

const skills: [string, string][] = [
  ["Core", "Java, Spring Boot, Spring Security, JPA/Hibernate, REST APIs, PostgreSQL, Redis, Apache Kafka, RabbitMQ"],
  ["Platform", "Docker, Kubernetes, GitLab CI, SonarQube, Grafana, Prometheus, JUnit, Mockito, Swagger/OpenAPI, Git"],
  ["Also", "TypeScript, React, Next.js · Claude Code and local LLM agents in the daily workflow · exploring Go, Rust, Tauri"],
]

const education: [string, string, string][] = [
  ["MSc Informatics Engineering & Web Technology (in progress)", "Universidade Aberta", "2024 – present"],
  ["Graduate Coursework in Software Engineering", "Instituto Superior de Engenharia do Porto (ISEP)", "2023 – 2024"],
  ["BSc Communication and Multimedia", "Universidade de Trás-os-Montes e Alto Douro (UTAD)", "2020 – 2023"],
]

const projects: [string, string, string, string?][] = [
  ["Nexo", "Portugal's public transport on one live map, 34 operators, RAPTOR routing", "Go, TypeScript, Svelte", "nexo.peixotomdb.com/landing/"],
  ["Dead Letter", "SMTP sink that captures outgoing email in local development", "Go, Docker, Next.js", "deadletter.peixotomdb.com"],
  ["Tools", "33 developer and design utilities that run in the browser", "Next.js, React, TypeScript", "tools.peixotomdb.com"],
  ["Pulse", "macOS menu-bar monitor for real-time system metrics", "Tauri, Rust, React (work in progress)"],
]

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h2 className="mb-3 mt-9 border-t border-foreground/25 pt-3 text-[15px] font-semibold text-foreground">
      {children}
    </h2>
  )
}

function EntryHeader({
  role,
  org,
  dates,
  location,
}: {
  role: string
  org: string
  dates: string
  location?: string
}) {
  return (
    <div className="mb-1">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4">
        <span className="font-semibold text-foreground">{role}</span>
        <span className="text-xs text-muted-foreground">{dates}</span>
      </div>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4">
        <span className="text-sm text-accent">{org}</span>
        {location && <span className="text-xs text-muted-foreground">{location}</span>}
      </div>
    </div>
  )
}

function Phase({ label, dates }: { label: string; dates: string }) {
  return (
    <div className="mt-3 flex flex-wrap items-baseline justify-between gap-x-4">
      <span className="text-sm italic text-foreground">{label}</span>
      <span className="text-xs text-muted-foreground">{dates}</span>
    </div>
  )
}

function Bullets({ items }: { items: ReactNode[] }) {
  return (
    <ul className="mt-1.5 space-y-1.5">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2 text-sm leading-relaxed text-muted-foreground">
          <span className="mt-[7px] h-1 w-1 shrink-0 bg-accent" aria-hidden="true" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  )
}

function B({ children }: { children: ReactNode }) {
  return <strong className="font-semibold text-foreground">{children}</strong>
}

export default function CvPage() {
  return (
    <main className="min-h-screen bg-background py-10 print:py-0">
      {/* Toolbar (hidden on print) */}
      <div className="mx-auto mb-8 flex max-w-3xl items-center justify-between px-6 print:hidden">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-[15px] text-muted-foreground transition-colors hover:text-accent"
        >
          <ArrowLeft className="h-4 w-4" /> Home
        </Link>
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 border border-foreground px-5 py-2 text-sm font-medium text-foreground transition-colors hover:border-accent hover:text-accent"
          >
            <Printer className="h-4 w-4" /> Print
          </button>
          <a
            href="/Luis_Peixoto_CV.pdf"
            download
            className="inline-flex items-center gap-2 bg-foreground px-5 py-2 text-sm font-medium text-background transition-colors hover:bg-accent"
          >
            <Download className="h-4 w-4" /> Download PDF
          </a>
        </div>
      </div>

      {/* CV sheet */}
      <article className="mx-auto max-w-3xl bg-background px-6 text-foreground print:max-w-none print:px-0">
        {/* Header */}
        <header className="flex items-start justify-between gap-6">
          <div className="min-w-0">
            <h1 className="font-sans text-4xl font-semibold tracking-tight text-foreground md:text-5xl">
              Luís Peixoto
            </h1>
            <p className="mt-1.5 text-lg text-foreground">
              <span className="text-accent">Backend Software Engineer</span>
              <span className="text-muted-foreground"> · Java · Spring Boot · Distributed Systems</span>
            </p>
            <p className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
              <a href="mailto:peixotomdb@gmail.com" className="hover:text-accent">peixotomdb@gmail.com</a>
              <span className="text-border">|</span>
              <a href="https://www.linkedin.com/in/peixotomdb" className="hover:text-accent">linkedin.com/in/peixotomdb</a>
              <span className="text-border">|</span>
              <a href="https://github.com/peixotomdb" className="hover:text-accent">github.com/peixotomdb</a>
            </p>
            <p className="mt-1.5 text-xs text-muted-foreground">
              Vila Real, Portugal · Open to relocation or full remote · EU citizen, no permit sponsorship needed
            </p>
          </div>
          <Image
            src="/portrait.jpg"
            alt="Luís Peixoto"
            width={900}
            height={1200}
            priority
            className="objective hidden h-[140px] w-[112px] shrink-0 border border-border object-cover sm:block"
          />
        </header>

        {/* Lead */}
        <p className="mt-6 text-sm leading-relaxed text-foreground">
          Backend engineer who owns high-scale Java systems and fixes what others get stuck on. Three years at CEiiA,
          one of Portugal&apos;s leading engineering centres, building AYR, an award-winning platform that validates
          millions of trips a month.
        </p>

        {/* Achievements */}
        <SectionTitle>Selected achievements</SectionTitle>
        <Bullets
          items={[
            <>Held a production platform alone for ~6 months after every senior engineer left: <B>15 microservices, 20+ incidents resolved, zero data loss</B>, millions of trips flowing uninterrupted</>,
            <>Cached authentication tokens in Redis, cutting latency on <B>every request from ~300ms to ~14ms (20× faster)</B></>,
            <>Built and ran <B>15+ Spring Boot microservices</B> processing hundreds of millions of records</>,
            <>Wired AI agents into daily engineering: a 30-minute daily report now writes itself, test suites land in <B>hours instead of days</B>, scaffolding in minutes instead of ~5 hours</>,
          ]}
        />

        {/* Experience */}
        <SectionTitle>Professional experience</SectionTitle>

        <EntryHeader
          role="Software Engineer"
          org="CEiiA · Centre of Engineering and Product Development"
          dates="Jul 2023 – Present"
          location="Matosinhos, PT (Hybrid)"
        />
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
          Intern to owner of large-scale distributed systems, now driving cross-team technical direction alongside the
          platform architect.
        </p>

        <Phase label="Cross-team technical direction" dates="2026 – present" />
        <Bullets
          items={[
            "Works with the platform architect on solution design and architectural trade-offs across multiple products",
            "Decomposes monoliths into clean, performant microservices with authentication and security hardening built in",
            "The engineer other teams turn to for the problems with no obvious answer",
          ]}
        />

        <Phase label="AYR · Sustainability & Mobility Platform" dates="2024 – 2025" />
        <p className="mt-1 text-xs italic leading-relaxed text-muted-foreground">
          New European Bauhaus prize winner: quantifies CO₂ avoided through sustainable mobility, rewarded as digital
          green credits.
        </p>
        <Bullets
          items={[
            "Built trip validation and digital wallet services: accounts, transfers, purchases, green-credit rewards",
            "Delivered real-time CO₂-avoided monitoring across multiple calculation methods, and onboarded major partners",
            <>Designed REST APIs on PostgreSQL; removed <B>N+1 query patterns</B> by batching data access</>,
            "Ran async messaging on Kafka with Redis caching; tracked health and SLA compliance in Grafana and Prometheus",
            "Built internal React/Next.js dashboards for trip processing and errors, ending reliance on the frontend team",
          ]}
        />

        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          <em className="text-foreground">Data ingestion &amp; monitoring</em>{" "}
          <span className="text-xs">(2023 – 2024)</span>: ingestion, processing and monitoring of trips and
          vehicles. Foundational Spring Boot and PostgreSQL work at scale.
        </p>

        <div className="mt-5">
          <EntryHeader role="Software Developer Intern" org="CEiiA" dates="Mar 2023 – Jun 2023" location="Matosinhos, PT" />
          <Bullets
            items={[
              <>Designed and delivered a backoffice trips-and-vehicles viewer for an emerging mobility project. Led to an <B>immediate full-time hire</B></>,
            ]}
          />
        </div>

        <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
          <B>Earlier:</B> Frontend Developer intern at X-Plora, responsive front-ends and reusable components in Vue.js
          (2022) · Student Technology &amp; Digital Innovation, UTAD Student Associations (2022 – 2023).
        </p>

        {/* Skills */}
        <SectionTitle>Technical skills</SectionTitle>
        <dl className="space-y-1.5">
          {skills.map(([k, v]) => (
            <div key={k} className="flex flex-col gap-0.5 sm:flex-row sm:gap-4">
              <dt className="shrink-0 text-sm font-semibold text-foreground sm:w-20">{k}</dt>
              <dd className="text-sm text-muted-foreground">{v}</dd>
            </div>
          ))}
        </dl>

        {/* Projects */}
        <SectionTitle>Personal projects</SectionTitle>
        <div className="space-y-2">
          {projects.map(([name, desc, stack, url]) => (
            <p key={name} className="text-sm">
              <B>{name}</B>
              <span className="text-muted-foreground"> · {desc} </span>
              <span className="italic text-muted-foreground">{stack}</span>
              {url && (
                <>
                  {" "}
                  <a href={`https://${url}`} className="text-accent hover:underline">
                    {url.split("/")[0]}
                  </a>
                </>
              )}
            </p>
          ))}
        </div>

        {/* Education, Certifications & Languages */}
        <SectionTitle>Education, certifications and languages</SectionTitle>
        <div className="space-y-2.5">
          {education.map(([deg, inst, dates]) => (
            <div key={deg}>
              <div className="flex flex-wrap items-baseline justify-between gap-x-4">
                <span className="text-sm font-semibold text-foreground">{deg}</span>
                <span className="text-xs text-muted-foreground">{dates}</span>
              </div>
              <span className="text-sm text-accent">{inst}</span>
            </div>
          ))}
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          <B>Certifications:</B> CMMI · SOLID Architecture · Java Development Course
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          <B>Languages:</B> Portuguese (native) · English (professional fluency, C1)
        </p>

        <div className="h-12 print:hidden" />
      </article>
    </main>
  )
}
